import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

import { BreadcrumbHeader, FilterTabs, EmptyState, ActionMenu, DataTable } from '../../components/shared';
import ImportLocationsModal from '../../components/shared/ImportLocationsModal';
import { parts, stockLevels, inventoryAlerts, warehouses as initialWarehouses, customers, contractAssets } from '../../services/dataClient';
import '../../styles/design-tokens.css';
import { fieldSx } from '../../theme/fieldStyles';

// ─── Shared style helpers ──────────────────────────────────────────────────────

// ─── Parts tab: Location filter tabs ──────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";const LOC_TABS = [
{ label: 'All', value: 'all' },
{ label: 'FSL', value: 'FSL' },
{ label: 'Warehouse', value: 'Warehouse' },
{ label: 'On-Site', value: 'On-Site' }];

// ─── Locations tab: Type filter tabs ──────────────────────────────────────────
const TYPE_TABS = [
{ label: 'All', value: 'All' },
{ label: 'Warehouse', value: 'Warehouse' },
{ label: 'FSL', value: 'FSL' },
{ label: 'On-Site', value: 'On-Site' }];

const REGION_OPTIONS = [
'North America - East',
'North America - West',
'North America - Central',
'Europe - West',
'Europe - East',
'Asia Pacific',
'Middle East & Africa',
'Latin America'];

const EMPTY_FORM = {
  name: '',
  type: 'Warehouse',
  region: '',
  customerName: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  notes: '',
  active: true
};

// ─── Parts tab: Summary bar ────────────────────────────────────────────────────
function SummaryBar({ totalParts, totalUnits, lowStock, outOfStock }) {
  const stats = [
  { label: 'Total Parts', value: totalParts, color: 'var(--grey-900)' },
  { label: 'Total Stock Units', value: totalUnits, color: 'var(--grey-900)' },
  { label: 'Low Stock Alerts', value: lowStock, color: lowStock > 0 ? '#BF360C' : 'var(--grey-900)' },
  { label: 'Out of Stock', value: outOfStock, color: outOfStock > 0 ? '#B71C1C' : 'var(--grey-900)' }];

  return (
    <ContentCard sx={{ display: 'flex', overflow: 'hidden' }}>
      {stats.map((s, i) =>
      <Box
        key={s.label}
        sx={{ flex: 1, px: 3, py: 2, borderRight: i < stats.length - 1 ? '1px solid var(--grey-200)' : 'none' }}>
        
          <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: s.color, lineHeight: 1.2 }}>
            {s.value}
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mt: '4px' }}>
            {s.label}
          </Typography>
        </Box>
      )}
    </ContentCard>);

}

// ─── Smart Stocking parts set (active/expiring contracts only) ────────────────
const SMART_STOCK_PARTS = new Set(
  contractAssets.filter((a) => a.status !== 'Expired').map((a) => a.partNumber)
);

// ─── Parts tab: Inventory table ────────────────────────────────────────────────
const COLUMNS = [
{ field: 'partNumber', headerName: 'Part #', sortable: true },
{ field: 'description', headerName: 'Part Description', sortable: true },
{ field: 'category', headerName: 'Category', sortable: true },
{ field: 'oem', headerName: 'OEM', sortable: true },
{ field: 'totalStock', headerName: 'Total Quantity', sortable: true },
{ field: 'fslStock', headerName: 'FSL Stock', sortable: true },
{ field: 'warehouseStock', headerName: 'Warehouse Stock', sortable: true },
{ field: 'onSiteStock', headerName: 'On-Site Stock', sortable: true },
{ field: 'reorderThreshold', headerName: 'Threshold', sortable: true },
{ field: 'alertCount', headerName: 'Alerts', sortable: false },
{ field: '_actions', headerName: '', sortable: false }];

function InventoryTable({ rows, sortConfig, onSort, onRowClick, smartStockSet }) {
  const navigate = useNavigate();
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {COLUMNS.map((col) => {
              const isSorted = sortConfig.field === col.field;
              return (
                <TableCell
                  key={col.field}
                  onClick={col.sortable ? () => onSort(col.field) : undefined}
                  sx={{ backgroundColor: '#eef2f6',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: isSorted ? '#009688' : 'var(--grey-700)',
                    whiteSpace: 'nowrap',
                    borderBottom: '1px solid var(--grey-200)',
                    py: '12px',
                    px: 2,
                    cursor: col.sortable ? 'pointer' : 'default',
                    userSelect: 'none',
                    '&:hover': col.sortable ? { color: '#009688' } : undefined
                  }}>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {col.headerName}
                    {isSorted && (
                    sortConfig.direction === 'asc' ?
                    <ArrowUpwardIcon sx={{ fontSize: 12 }} /> :
                    <ArrowDownwardIcon sx={{ fontSize: 12 }} />)
                    }
                  </Box>
                </TableCell>);

            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length === 0 ?
          <TableRow>
              <TableCell colSpan={COLUMNS.length} sx={{ border: 'none', py: 4 }}>
                <Typography sx={{ color: 'var(--grey-400)', fontSize: '0.875rem', textAlign: 'center' }}>
                  No parts match your search or filter.
                </Typography>
              </TableCell>
            </TableRow> :

          rows.map((row) => {
            const belowThreshold = row.totalStock < row.reorderThreshold;
            const nearThreshold = !belowThreshold && row.totalStock < row.reorderThreshold * 1.2;
            const qtyColor = belowThreshold ? '#B71C1C' : nearThreshold ? '#E65100' : 'var(--grey-800)';
            return (
              <TableRow
                key={row.id}
                onClick={() => onRowClick(row)}
                sx={{ height: 48,
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--grey-200)',
                  '&:last-child td': { borderBottom: 'none' },
                  '&:hover': { backgroundColor: '#f8fafc' }
                }}>
                
                  <TableCell sx={{ px: 2, py: 0, border: 'none' }}>
                    <Typography sx={{ fontSize: '0.875rem', color: '#009688', fontWeight: 500, cursor: 'pointer' }}>
                      {row.partNumber}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 0, border: 'none', fontSize: '0.875rem', color: 'var(--grey-800)' }}>
                    {row.description}
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 0, border: 'none', fontSize: '0.875rem', color: 'var(--grey-800)' }}>
                    {row.category}
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 0, border: 'none', fontSize: '0.875rem', color: 'var(--grey-800)' }}>
                    {row.oem ?? '—'}
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 0, border: 'none' }}>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: belowThreshold || nearThreshold ? 700 : 400, color: qtyColor }}>
                      {row.totalStock}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 0, border: 'none', fontSize: '0.875rem', color: 'var(--grey-800)' }}>
                    {row.fslStock}
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 0, border: 'none', fontSize: '0.875rem', color: 'var(--grey-800)' }}>
                    {row.warehouseStock}
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 0, border: 'none', fontSize: '0.875rem', color: 'var(--grey-800)' }}>
                    {row.onSiteStock}
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 0, border: 'none', fontSize: '0.875rem', color: 'var(--grey-800)' }}>
                    {row.reorderThreshold}
                  </TableCell>
                  <TableCell sx={{ px: 2, py: 0, border: 'none' }}>
                    {row.alertCount > 0 ?
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', py: '3px', borderRadius: '12px', backgroundColor: '#FFEBEE' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#B71C1C', lineHeight: 1 }}>
                          {row.alertCount} Alert{row.alertCount > 1 ? 's' : ''}
                        </Typography>
                      </Box> :

                  <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>
                  }
                  </TableCell>
                  <TableCell sx={{ px: 1, py: 0, border: 'none', width: 56 }} onClick={(e) => e.stopPropagation()}>
                    <ActionMenu
                    hideUntilRowHover
                    actions={[
                    { label: 'View Detail', onClick: () => navigate(`/inventory/search/${row.id}`) },
                    ...(row.alertCount > 0 ?
                    [{ label: 'View Alerts', onClick: () => navigate(`/inventory/search/${row.id}#alerts`) }] :
                    []),
                    ...(smartStockSet.has(row.partNumber) ?
                    [{ label: 'Smart Stocking', onClick: () => navigate(`/inventory/smart-stocking?part=${row.partNumber}`) }] :
                    [])]
                    } />
                  
                  </TableCell>
                </TableRow>);

          })
          }
        </TableBody>
      </Table>
    </TableContainer>);

}

// ─── Locations tab: Add / Edit modal ──────────────────────────────────────────
function LocationModal({ open, mode, initial, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_FORM);

  const handleEnter = () =>
  setForm(
    mode === 'edit' && initial ?
    {
      name: initial.name,
      type: initial.type ?? 'Warehouse',
      region: initial.region ?? '',
      customerName: initial.customerName ?? '',
      address: initial.address,
      city: initial.city,
      state: initial.state,
      zip: initial.zip ?? '',
      contactName: initial.contactName,
      contactEmail: initial.contactEmail,
      contactPhone: initial.contactPhone ?? '',
      notes: initial.notes ?? '',
      active: initial.active
    } :
    EMPTY_FORM
  );

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const isValid =
  form.name && form.region && form.address && form.city && form.state &&
  form.zip && form.contactName && form.contactEmail && (
  form.type !== 'On-Site' || form.customerName);

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {if (reason !== 'backdropClick') onClose();}}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onEnter: handleEnter }}>
      
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {mode === 'edit' ? 'Edit Location' : 'Add Location'}
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        <TextField size="small" label="Name" value={form.name} onChange={set('name')} fullWidth required sx={fieldSx} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', minWidth: 36 }}>Type</Typography>
          <ToggleButtonGroup
            exclusive size="small" value={form.type}
            onChange={(_, val) => {if (val) setForm((prev) => ({ ...prev, type: val }));}}
            sx={{ '& .MuiToggleButton-root': {
                textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600, px: 2, py: '4px',
                borderColor: 'var(--grey-200)', color: 'var(--grey-500)',
                '&.Mui-selected': { backgroundColor: '#009688', color: '#fff', borderColor: '#009688' },
                '&.Mui-selected:hover': { backgroundColor: '#00796B' }
              }
            }}>
            
            <ToggleButton value="Warehouse">Warehouse</ToggleButton>
            <ToggleButton value="FSL">FSL</ToggleButton>
            <ToggleButton value="On-Site">On-Site</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <FormControl size="small" fullWidth required sx={fieldSx}>
          <InputLabel>Region</InputLabel>
          <Select
            label="Region" value={form.region}
            onChange={(e) => setForm((prev) => ({ ...prev, region: e.target.value }))}
            sx={{ fontSize: '0.875rem' }}>
            
            {REGION_OPTIONS.map((r) =>
            <MenuItem key={r} value={r} sx={{ fontSize: '0.875rem' }}>{r}</MenuItem>
            )}
          </Select>
        </FormControl>

        {form.type === 'On-Site' &&
        <FormControl size="small" fullWidth required sx={fieldSx}>
            <InputLabel>Customer</InputLabel>
            <Select
            label="Customer" value={form.customerName}
            onChange={(e) => setForm((prev) => ({ ...prev, customerName: e.target.value }))}
            sx={{ fontSize: '0.875rem' }}>
            
              {customers.filter((c) => c.active).map((c) =>
            <MenuItem key={c.id} value={c.name} sx={{ fontSize: '0.875rem' }}>{c.name}</MenuItem>
            )}
            </Select>
          </FormControl>
        }

        <TextField size="small" label="Address" value={form.address} onChange={set('address')} fullWidth required sx={fieldSx} />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField size="small" label="City" value={form.city} onChange={set('city')} fullWidth required sx={fieldSx} />
          <TextField size="small" label="State" value={form.state} onChange={set('state')} sx={{ width: 100, ...fieldSx }} required />
          <TextField size="small" label="Zip Code" value={form.zip} onChange={set('zip')} sx={{ width: 120, ...fieldSx }} required />
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField size="small" label="Contact Name" value={form.contactName} onChange={set('contactName')} fullWidth required sx={fieldSx} />
          <TextField size="small" label="Contact Email" type="email" value={form.contactEmail} onChange={set('contactEmail')} fullWidth required sx={fieldSx} />
        </Box>

        <TextField size="small" label="Contact Phone (optional)" value={form.contactPhone} onChange={set('contactPhone')} fullWidth sx={fieldSx} />
        <TextField size="small" label="Notes (optional)" value={form.notes} onChange={set('notes')} multiline rows={2} fullWidth sx={fieldSx} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>Status</Typography>
          <ToggleButtonGroup
            exclusive size="small" value={form.active ? 'active' : 'inactive'}
            onChange={(_, val) => {if (val) setForm((prev) => ({ ...prev, active: val === 'active' }));}}
            sx={{ '& .MuiToggleButton-root': {
                textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600, px: 2, py: '4px',
                borderColor: 'var(--grey-200)', color: 'var(--grey-500)',
                '&.Mui-selected': { backgroundColor: '#009688', color: '#fff', borderColor: '#009688' },
                '&.Mui-selected:hover': { backgroundColor: '#00796B' }
              }
            }}>
            
            <ToggleButton value="active">Active</ToggleButton>
            <ToggleButton value="inactive">Inactive</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          
          Cancel
        </Typography>
        <Button
          variant="contained" size="small" disabled={!isValid} onClick={() => onSave(form)}
          sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
            textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 2
          }}>
          
          {mode === 'edit' ? 'Save Changes' : 'Add Location'}
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function InventorySearchPage() {
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();

  // ── Tab state ────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('parts');

  // ── Parts tab state ──────────────────────────────────────────────────────────
  const [search, setSearch] = useState('');
  const [locTab, setLocTab] = useState('all');
  const [sortConfig, setSortConfig] = useState({ field: 'partNumber', direction: 'asc' });

  // ── Locations tab state ──────────────────────────────────────────────────────
  const [locations, setLocations] = useState(initialWarehouses);
  const [typeFilter, setTypeFilter] = useState('All');
  const [showArchived, setShowArchived] = useState(false);
  const [locSearch, setLocSearch] = useState('');
  const [locSortConfig, setLocSortConfig] = useState({ field: 'name', direction: 'asc' });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editTarget, setEditTarget] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [banner, setBanner] = useState('');

  // Pre-select Locations tab if ?tab=locations in the URL, or if navigated from Customers page
  useEffect(() => {
    const params = new URLSearchParams(routerLocation.search);
    if (params.get('tab') === 'locations') {
      setActiveTab('locations');
      return;
    }
    const cf = routerLocation.state?.customerFilter;
    if (cf) {
      setActiveTab('locations');
      setTypeFilter('On-Site');
      setLocSearch(cf);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showBanner = (msg) => {setBanner(msg);setTimeout(() => setBanner(''), 4000);};

  // ── Parts tab: computed data ─────────────────────────────────────────────────
  const partRows = useMemo(() => parts.map((p) => {
    const levels = stockLevels.filter((sl) => sl.partNumber === p.partNumber);
    const fslStock = levels.filter((sl) => sl.locationType === 'FSL').reduce((s, sl) => s + sl.quantityOnHand, 0);
    const warehouseStock = levels.filter((sl) => sl.locationType === 'Warehouse').reduce((s, sl) => s + sl.quantityOnHand, 0);
    const onSiteStock = levels.filter((sl) => sl.locationType === 'On-Site').reduce((s, sl) => s + sl.quantityOnHand, 0);
    const totalStock = fslStock + warehouseStock + onSiteStock;
    const alertCount = inventoryAlerts.filter((a) => a.partNumber === p.partNumber).length;
    const hasFSL = levels.some((sl) => sl.locationType === 'FSL');
    const hasWarehouse = levels.some((sl) => sl.locationType === 'Warehouse');
    const hasOnSite = levels.some((sl) => sl.locationType === 'On-Site');
    return { ...p, fslStock, warehouseStock, onSiteStock, totalStock, alertCount, hasFSL, hasWarehouse, hasOnSite };
  }), []);

  const totalStockUnits = useMemo(() => stockLevels.reduce((s, sl) => s + sl.quantityOnHand, 0), []);
  const uniqueLocations = useMemo(() => new Set(stockLevels.map((sl) => sl.locationName)).size, []);
  const lowStockCount = inventoryAlerts.filter((a) => a.alertType === 'Low Stock').length;
  const outOfStockCount = inventoryAlerts.filter((a) => a.alertType === 'Out of Stock').length;

  const filteredPartRows = useMemo(() => {
    let rows = partRows;
    if (locTab === 'FSL') rows = rows.filter((r) => r.hasFSL);
    if (locTab === 'Warehouse') rows = rows.filter((r) => r.hasWarehouse);
    if (locTab === 'On-Site') rows = rows.filter((r) => r.hasOnSite);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((r) =>
      r.partNumber.toLowerCase().includes(q) ||
      r.description.toLowerCase().includes(q) ||
      r.category.toLowerCase().includes(q)
      );
    }
    return [...rows].sort((a, b) => {
      const av = a[sortConfig.field];
      const bv = b[sortConfig.field];
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }, [partRows, locTab, search, sortConfig]);

  const handlePartsSort = (field) => {
    setSortConfig((prev) =>
    prev.field === field ?
    { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' } :
    { field, direction: 'asc' }
    );
  };

  // ── Locations tab: computed data ─────────────────────────────────────────────
  const partCountMap = useMemo(() =>
  Object.fromEntries(
    locations.map((w) => [w.id, stockLevels.filter((sl) => sl.locationName === w.name).length])
  ),
  [locations]);

  const filteredLocations = useMemo(() => {
    const q = locSearch.trim().toLowerCase();
    return [...locations]
    .filter((w) => showArchived ? !w.active : w.active)
    .filter((w) => typeFilter === 'All' || w.type === typeFilter)
    .filter((w) =>
    !q ||
    w.name.toLowerCase().includes(q) ||
    w.city.toLowerCase().includes(q) ||
    w.state.toLowerCase().includes(q) ||
    (w.region ?? '').toLowerCase().includes(q) ||
    (w.customerName ?? '').toLowerCase().includes(q)
    )
    .sort((a, b) => {
      const av = a[locSortConfig.field] ?? '';
      const bv = b[locSortConfig.field] ?? '';
      const cmp = String(av).localeCompare(String(bv));
      return locSortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }, [locations, typeFilter, showArchived, locSearch, locSortConfig]);

  const handleLocSort = (field) =>
  setLocSortConfig((prev) => ({
    field,
    direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
  }));

  const handleLocationSave = (form) => {
    if (modalMode === 'edit') {
      setLocations((prev) => prev.map((w) => w.id === editTarget.id ? { ...w, ...form } : w));
      showBanner('Location updated successfully.');
    } else {
      const code = `WH-${String(locations.length + 1).padStart(2, '0')}`;
      setLocations((prev) => [...prev, { id: `wh-new-${Date.now()}`, code, ...form }]);
      showBanner('Location added successfully.');
    }
    setModalOpen(false);
  };

  const locationColumns = [
  {
    field: 'name',
    headerName: 'Name',
    sortable: true,
    renderCell: (row) =>
    <Box>
          <Typography
        onClick={(e) => {e.stopPropagation();navigate(`/inventory/stock-locations/${row.id}`);}}
        sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
        
            {row.name}
          </Typography>
          {row.region &&
      <Typography sx={{ fontSize: '0.72rem', color: 'var(--grey-500)', mt: '1px' }}>
              {row.region}
            </Typography>
      }
        </Box>

  },
  {
    field: 'type',
    headerName: 'Type',
    sortable: true,
    renderCell: (row) =>
    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>{row.type}</Typography>

  },
  {
    field: 'city',
    headerName: 'Location',
    sortable: true,
    renderCell: (row) =>
    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>
          {row.city}, {row.state}
        </Typography>

  },
  {
    field: '_parts',
    headerName: 'Active Parts',
    sortable: false,
    renderCell: (row) =>
    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>
          {partCountMap[row.id] ?? 0}
        </Typography>

  },
  { field: 'contactName', headerName: 'Contact Name', sortable: true },
  {
    field: 'contactEmail',
    headerName: 'Contact Email',
    sortable: false,
    renderCell: (row) =>
    <Typography
      component="a"
      href={`mailto:${row.contactEmail}`}
      onClick={(e) => e.stopPropagation()}
      sx={{ fontSize: '0.875rem', color: '#009688', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
      
          {row.contactEmail}
        </Typography>

  },
  {
    field: '_actions',
    headerName: '',
    sortable: false,
    renderCell: (row) =>
    <Box onClick={(e) => e.stopPropagation()}>
          <ActionMenu
        hideUntilRowHover
        actions={[
        { label: 'View Details', onClick: () => navigate(`/inventory/stock-locations/${row.id}`) },
        { label: 'Edit', onClick: () => {setEditTarget(row);setModalMode('edit');setModalOpen(true);} }]
        } />
      
        </Box>

  }];

  // ── Dynamic subtitle ─────────────────────────────────────────────────────────
  const subtitle = activeTab === 'parts' ?
  `${parts.length} parts across ${uniqueLocations} locations` :
  `${filteredLocations.length} of ${locations.filter((w) => w.active).length} active locations`;

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Success banner (Locations tab) */}
      {banner &&
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: '8px', px: 2, py: '12px' }}>
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>{banner}</Typography>
        </Box>
      }

      {/* Header */}
      <BreadcrumbHeader
        title="Inventory"
        subtitle={subtitle}
        crumbs={[{ label: 'Inventory' }]} />

      {/* Tab bar */}
      <ContentCard sx={{ px: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          textColor="secondary"
          indicatorColor="secondary"
          sx={{ minHeight: 44,
            '& .MuiTab-root': {
              fontSize: '0.875rem',
              fontWeight: 500,
              textTransform: 'none',
              color: 'var(--grey-600)',
              minHeight: 44,
              py: '10px'
            },
            '& .Mui-selected': { color: 'var(--secondary-main)', fontWeight: 600 },
            '& .MuiTabs-indicator': { backgroundColor: 'var(--secondary-main)' }
          }}>
          
          <Tab value="parts" label="Parts" disableRipple />
          <Tab value="locations" label="Stock Locations" disableRipple />
        </Tabs>
      </ContentCard>

      {/* ── Parts tab content ──────────────────────────────────────────────────── */}
      {activeTab === 'parts' &&
      <>
          <SummaryBar
          totalParts={parts.length}
          totalUnits={totalStockUnits}
          lowStock={lowStockCount}
          outOfStock={outOfStockCount} />

          <ContentCard sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FilterTabs tabs={LOC_TABS} value={locTab} onChange={setLocTab} />

            <TextField
            placeholder="Search by Part #, Description, or Category…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment:
              <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'var(--grey-400)', fontSize: 22 }} />
                  </InputAdornment>

            }}
            sx={{ '& .MuiOutlinedInput-root': {
                fontSize: '0.9375rem',
                '& fieldset': { borderColor: 'var(--grey-200)' },
                '&:hover fieldset': { borderColor: 'var(--grey-400)' },
                '&.Mui-focused fieldset': { borderColor: '#009688' }
              },
              '& .MuiInputBase-input': { py: '11px' }
            }} />

            <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
              <InventoryTable
              rows={filteredPartRows}
              sortConfig={sortConfig}
              onSort={handlePartsSort}
              onRowClick={(row) => navigate(`/inventory/search/${row.id}`)}
              smartStockSet={SMART_STOCK_PARTS} />
            
            </Box>
          </ContentCard>
        </>
      }

      {/* ── Locations tab content ──────────────────────────────────────────────── */}
      {activeTab === 'locations' &&
      <ContentCard sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Type tabs */}
          <FilterTabs tabs={TYPE_TABS} value={typeFilter} onChange={setTypeFilter} />

          {/* Search + Show Archived toggle + action buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
            size="small"
            placeholder="Search by Name, City, or State…"
            value={locSearch}
            onChange={(e) => setLocSearch(e.target.value)}
            InputProps={{
              startAdornment:
              <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} />
                  </InputAdornment>

            }}
            sx={{ minWidth: 400, maxWidth: 600, backgroundColor: 'var(--paper)', borderRadius: 1, ...fieldSx }} />

            <Box sx={{ flex: 1 }} />

            <Box
            sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
            onClick={() => setShowArchived((prev) => !prev)}>
            
              <Switch
              checked={showArchived}
              size="small"
              onChange={(e) => setShowArchived(e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#009688' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#009688' }
              }} />
            
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-600)', userSelect: 'none' }}>
                Show Archived
              </Typography>
            </Box>

            <Button
            variant="outlined" size="small"
            onClick={() => setImportOpen(true)}
            sx={{ borderColor: '#009688', color: '#009688',
              '&:hover': { borderColor: '#00796B', backgroundColor: '#f0faf9' },
              textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none'
            }}>
            
              Import Inventory
            </Button>
            <Button
            variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => {setModalMode('create');setEditTarget(null);setModalOpen(true);}}
            sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
              textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none'
            }}>
            
              Add Location
            </Button>
          </Box>

          <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
            {filteredLocations.length === 0 ?
          <EmptyState title="No locations found" description="Try adjusting your filter or search." /> :

          <DataTable
            columns={locationColumns}
            data={filteredLocations}
            sortConfig={locSortConfig}
            onSort={handleLocSort} />

          }
          </Box>
        </ContentCard>
      }

      {/* Location add/edit modal */}
      <LocationModal
        open={modalOpen}
        mode={modalMode}
        initial={editTarget}
        onClose={() => setModalOpen(false)}
        onSave={handleLocationSave} />

      {/* Import modal */}
      <ImportLocationsModal
        open={importOpen}
        defaultType={typeFilter === 'All' ? 'Warehouse' : typeFilter}
        onClose={() => setImportOpen(false)}
        onImported={(type, newCount, updateCount) => {
          setImportOpen(false);
          const msgs = [];
          if (newCount > 0) msgs.push(`${newCount} new`);
          if (updateCount > 0) msgs.push(`${updateCount} updated`);
          showBanner(`Imported ${msgs.join(' · ')} inventory record${newCount + updateCount !== 1 ? 's' : ''} for ${type} locations.`);
        }} />
      
    </Box>);

}